import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import InfoChip from './InfoChip';
import './ApplicationReview.css';
import './LeaseSigning.css';

function LeaseSigning() {
  const { id } = useParams();

  // Application context
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lease state
  const [leaseDocs, setLeaseDocs] = useState([]);
  const [leaseUploadFile, setLeaseUploadFile] = useState(null);
  const [leaseUploadLoading, setLeaseUploadLoading] = useState(false);
  const [leaseGenerateLoading, setLeaseGenerateLoading] = useState(false);
  const [leaseGenerateError, setLeaseGenerateError] = useState('');
  const [leaseReadyState, setLeaseReadyState] = useState(null);
  const [leaseAutoGenEnabled, setLeaseAutoGenEnabled] = useState(true);
  const [selectedLeaseId, setSelectedLeaseId] = useState(null);

  // E-sign state
  const [esignRequest, setEsignRequest] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showLandlordSignButton, setShowLandlordSignButton] = useState(false);
  const [esignError, setEsignError] = useState('');
  const [esignModalUrl, setEsignModalUrl] = useState('');

  // Derived lease status
  const esignStatus = esignRequest?.status || '';
  const isEsignComplete = esignStatus === 'completed'
    || (leaseDocs || []).some((doc) => ['signed', 'completed'].includes(doc.status));
  const hasSignedLease = ['tenant_signed', 'completed'].includes(esignStatus)
    || isEsignComplete
    || (leaseDocs || []).some((doc) => ['tenant_signed', 'signed', 'completed'].includes(doc.status));
  const landlordSigned = esignStatus === 'landlord_signed'
    || isEsignComplete
    || (leaseDocs || []).some((doc) => ['landlord_signed', 'signed', 'completed'].includes(doc.status));
  const hasLeaseDoc = (leaseDocs || []).length > 0;
  const leaseStatusLabel = esignStatus === 'completed'
    ? 'Completed (all signed)'
    : esignStatus === 'tenant_signed'
      ? 'Tenant signed (awaiting landlord)'
      : esignStatus === 'landlord_signed'
        ? 'Landlord signed (awaiting tenant)'
        : esignStatus === 'sent'
          ? 'Sent for signing'
          : hasLeaseDoc
            ? 'Lease generated (not signed)'
            : 'No lease uploaded';

  const isCompleted = esignStatus === 'completed' || application?.status === 'completed';

  // Current signing step
  const currentSigningStep = isCompleted ? 4
    : hasSignedLease ? 4
    : landlordSigned ? 3
    : esignRequest ? 2
    : hasLeaseDoc ? 2
    : 1;

  // ── Fetch application + lease data ──
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) { setLoading(false); return; }

    try {
      const [appRes, leaseRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/lease`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!appRes.ok) throw new Error('Failed to load application');
      const appData = await appRes.json();
      setApplication(appData.application);

      // Check approval status
      const status = appData.application?.status;
      if (!['approved', 'lease_tenant_signed', 'lease_drafted_landlord_signed', 'completed'].includes(status)) {
        setError('Application must be approved before lease signing.');
        setLoading(false);
        return;
      }

      const leaseData = leaseRes.ok ? await leaseRes.json() : { leases: [] };

      // Fetch e-sign status for first lease
      if (leaseData.leases && leaseData.leases.length > 0) {
        try {
          const esignRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${leaseData.leases[0].id}/esign`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (esignRes.ok) {
            const esignData = await esignRes.json();
            setEsignRequest(esignData.request || null);
          }
        } catch {}
      }

      // Fetch lease-ready state
      if (appData.application?.property_id) {
        try {
          const settingsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${appData.application.property_id}/settings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            setLeaseReadyState(settingsData.settings?.lease_ready_state || (settingsData.settings?.lease_ready ? 'ready' : 'incomplete'));
          }
        } catch {}
      }

      // Fetch auto-gen flag
      try {
        const flagRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/lease-autogen/flag`);
        if (flagRes.ok) {
          const flagData = await flagRes.json();
          setLeaseAutoGenEnabled(Boolean(flagData.enabled));
        }
      } catch {
        setLeaseAutoGenEnabled(true);
      }

      const rawLeases = leaseData.leases || [];
      const sortedLeases = rawLeases.slice().sort((a, b) =>
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      );
      setLeaseDocs(sortedLeases.slice(0, 1));

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Listen for BoldSign iframe postMessage events (signing complete, declined, etc.)
  useEffect(() => {
    if (!esignModalUrl) return;
    const handler = (event) => {
      // BoldSign sends { action: 'onDocumentSigned' } via postMessage
      const action = event.data?.action || (typeof event.data === 'string' ? event.data : '');
      if (['onDocumentSigned', 'onDocumentDeclined', 'onDocumentReassigned'].includes(action)) {
        setEsignModalUrl('');
        setEsignError('');
        fetchData();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [esignModalUrl, fetchData]);

  // Auto-select first lease
  useEffect(() => {
    if (!leaseDocs || leaseDocs.length === 0) return;
    if (!selectedLeaseId || !leaseDocs.find((doc) => doc.id === selectedLeaseId)) {
      setSelectedLeaseId(leaseDocs[0].id);
    }
  }, [leaseDocs, selectedLeaseId]);

  // ── Handlers ──

  const handleUploadLease = async () => {
    const token = localStorage.getItem('token');
    if (!token || !leaseUploadFile) return;
    setLeaseUploadLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('lease', leaseUploadFile);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/lease/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to upload lease');
      setLeaseDocs(prev => [data.lease, ...prev]);
      setLeaseUploadFile(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLeaseUploadLoading(false);
    }
  };

  const handleGenerateLease = async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    if (leaseReadyState !== 'ready') {
      setLeaseGenerateError('Listing is not lease-ready. Complete the lease-ready wizard to enable auto-generation.');
      return;
    }
    setLeaseGenerateLoading(true);
    setLeaseGenerateError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/lease/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to generate lease');
      if (data.lease) {
        setLeaseDocs(prev => [data.lease, ...prev]);
        setSelectedLeaseId(data.lease.id);
      }
      try {
        if (window.gtag) {
          window.gtag('event', 'lease_autogen_success', { event_category: 'lease', event_label: id, value: 1 });
        }
      } catch {}
    } catch (err) {
      setLeaseGenerateError(err.message || 'Failed to generate lease');
      try {
        if (window.gtag) {
          window.gtag('event', 'lease_autogen_error', { event_category: 'lease', event_label: id, value: 1 });
        }
      } catch {}
    } finally {
      setLeaseGenerateLoading(false);
    }
  };

  const openSignLink = (link) => {
    if (!link) return;
    if (window.location.protocol === 'http:' || window.location.hostname.includes('192.168.')) {
      window.open(link, '_blank');
      return;
    }
    setEsignModalUrl(link);
  };

  const handleSignAsLandlord = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Your session expired. Please log in again.');
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    const leaseId = selectedLeaseId || leaseDocs?.[0]?.id;
    if (!leaseId) {
      setEsignError('Select a lease first.');
      return;
    }

    let currentRequest = esignRequest;

    // Step 1: Create e-sign request if none exists
    if (!currentRequest) {
      setEsignError('Preparing signing document…');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/leases/${leaseId}/esign`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to start e-sign');
        currentRequest = data.request || null;
        setEsignRequest(currentRequest);
        setEsignError('');
      } catch (err) {
        setEsignError(err.message || 'Failed to start e-sign');
        return;
      }
    }

    if (!currentRequest?.lease_document_id) {
      setEsignError('E-sign request created but missing document reference. Please refresh.');
      return;
    }

    // Step 2: Get signing URL and open it
    setEsignError('Opening signing document…');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${currentRequest.lease_document_id}/esign/url?role=landlord`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to fetch signing link');
      if (data.url) {
        setEsignError('');
        openSignLink(data.url);
        return;
      }
      throw new Error('Signing link not available. Please try again.');
    } catch (err) {
      setEsignError(err.message || 'Failed to open signing link');
    }
  };



  const handleDeleteLease = async (leaseId) => {
    if (!leaseId) return;
    if (!window.confirm('Delete this lease document?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/leases/${leaseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete lease');
      setLeaseDocs(prev => prev.filter(item => item.id !== leaseId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMarkComplete = async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    if (!window.confirm('Mark this application as completed? This indicates the lease is fully executed.')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'completed' })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update status');
      }
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Render ──

  if (loading) {
    return (
      <main className="App-main application-review-page" style={{ padding: '2rem' }}>
        <div className="skeleton-line" style={{ width: '60%', height: '2rem', marginBottom: '1rem' }} aria-hidden="true" />
        <div className="skeleton-line" style={{ width: '40%', height: '1.2rem' }} aria-hidden="true" />
      </main>
    );
  }

  if (error && !application) {
    return (
      <main className="App-main application-review-page" style={{ padding: '2rem' }}>
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '1.25rem', textAlign: 'center' }}>
          <p style={{ color: '#DC2626', fontWeight: 600, margin: '0 0 0.5rem' }}>{error}</p>
          <Link to={`/landlord/application/${id}`} className="secondary-button" style={{ marginTop: '0.5rem' }}>
            ← Back to Application
          </Link>
        </div>
      </main>
    );
  }

  const StepIndicator = ({ step, current, label }) => {
    const isDone = current > step;
    const isActive = current === step;
    return (
      <div className={`lease-step-indicator${!isDone && !isActive ? ' lease-step-indicator--inactive' : ''}`}>
        <div className={`lease-step-circle lease-step-circle--${isDone ? 'done' : isActive ? 'active' : 'pending'}`}>
          {isDone ? '✓' : step}
        </div>
        <span className={`lease-step-label${isActive ? ' lease-step-label--active' : ''}`}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <main className="App-main application-review-page lease-signing-page">

      {/* Header */}
      <div className="lease-signing-header">
        <Link to={`/landlord/application/${id}`} className="lease-signing-back">
          ← Back to Application Review
        </Link>
        <h1 className="lease-signing-title">Lease & Signing</h1>
        <p className="lease-signing-applicant">
          {application?.full_name || 'Applicant'} — {application?.property_address || application?.title || 'Property'}
        </p>

        {/* Tenant details from application */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '0.35rem 1.5rem', marginTop: '0.5rem', fontSize: '0.82rem', color: '#4B5563'
        }}>
          {application?.move_in_date && (
            <span><strong>Move-in:</strong> {new Date(application.move_in_date).toLocaleDateString('en-CA')}</span>
          )}
          {application?.property_rent && (
            <span><strong>Rent:</strong> ${Number(application.property_rent).toLocaleString()}/mo</span>
          )}
          {application?.num_occupants && (
            <span><strong>Occupants:</strong> {application.num_occupants}</span>
          )}
          {application?.pet_info && (
            <span><strong>Pets:</strong> {application.pet_info}</span>
          )}
          {application?.current_employer && (
            <span><strong>Employer:</strong> {application.current_employer}</span>
          )}
          {application?.phone_number && (
            <span><strong>Phone:</strong> {application.phone_number}</span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <p style={{ color: '#DC2626', margin: 0, fontSize: '0.85rem' }}>{error}</p>
        </div>
      )}

      {/* Progress steps */}
      <div className="lease-step-progress">
        <StepIndicator step={1} current={currentSigningStep} label="Upload / Generate" />
        <StepIndicator step={2} current={currentSigningStep} label="Send for Signatures" />
        <StepIndicator step={3} current={currentSigningStep} label="Signing in Progress" />
        <StepIndicator step={4} current={currentSigningStep} label="Completed" />
      </div>

      {/* Step 1: Upload or generate lease */}
      <div className="lease-section">
        <h3>1. Upload or Generate Lease <InfoChip label="Need help?">Auto-generate the lease in Rentual, or upload your own PDF.</InfoChip></h3>

        {/* Current lease docs */}
        {leaseDocs && leaseDocs.length > 0 && (
          <ul className="document-list" style={{ marginBottom: '0.75rem' }}>
            {leaseDocs.map((leaseItem) => (
              <li key={leaseItem.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="link-button" onClick={() => {
                  const token = localStorage.getItem('token');
                  window.open(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/leases/${leaseItem.id}/download?token=${token}`, '_blank');
                }}>
                  {leaseItem.original_filename || 'Lease document'}
                </button>
                <span className="status-text">{leaseItem.status || 'uploaded'}</span>
                {!landlordSigned && !hasSignedLease && (
                  <button type="button" className="ghost-button ghost-button--tiny" onClick={() => handleDeleteLease(leaseItem.id)}>Delete</button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Lease selector */}
        {leaseDocs && leaseDocs.length > 1 && (
          <div className="form-group" style={{ maxWidth: '360px', marginBottom: '0.75rem' }}>
            <label>Selected lease</label>
            <select value={selectedLeaseId || leaseDocs[0].id} onChange={(e) => setSelectedLeaseId(Number(e.target.value))}>
              {leaseDocs.map((leaseItem) => (
                <option key={leaseItem.id} value={leaseItem.id}>{leaseItem.original_filename || `Lease #${leaseItem.id}`}</option>
              ))}
            </select>
          </div>
        )}

        {/* Generate or upload actions */}
        {!landlordSigned && !hasSignedLease && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <button className="primary-button" type="button" onClick={handleGenerateLease}
              disabled={leaseGenerateLoading || leaseReadyState !== 'ready' || !leaseAutoGenEnabled}>
              {leaseGenerateLoading ? 'Generating...' : 'Generate Lease'}
            </button>
            <p className="status-text" style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
              Ontario Standard Lease (Form 2229E). Other provinces: upload your own lease PDF.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <label className="secondary-button" style={{
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                fontSize: '0.85rem', margin: 0
              }}>
                📎 {leaseUploadFile ? leaseUploadFile.name : 'Choose file to upload'}
                <input type="file" accept=".pdf,.docx" onChange={(e) => setLeaseUploadFile(e.target.files?.[0] || null)}
                  style={{ display: 'none' }} />
              </label>
              {leaseUploadFile && (
                <button className="primary-button" type="button" onClick={handleUploadLease} disabled={leaseUploadLoading}
                  style={{ fontSize: '0.85rem' }}>
                  {leaseUploadLoading ? 'Uploading…' : 'Upload'}
                </button>
              )}
            </div>
          </div>
        )}

        {!leaseAutoGenEnabled && (
          <p className="status-text" style={{ marginTop: '0.5rem' }}>Lease auto-generation is currently disabled.</p>
        )}
        {(leaseReadyState === null || (leaseReadyState && leaseReadyState !== 'ready')) && (
          <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.85rem', background: '#F0FAFB', border: '1px solid #D1E7EC', borderRadius: '8px' }}>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 500, fontSize: '0.85rem', color: '#0F2933' }}>
              {leaseReadyState === null ? 'Lease setup not configured' : 'Lease setup incomplete'}
            </p>
            <p className="status-text" style={{ margin: '0 0 0.35rem' }}>
              Complete the lease-ready wizard to unlock auto-generation, or upload your own lease PDF.
            </p>
            <Link to={`/landlord/properties/edit/${application?.property_id}?focus=lease`}
              className="link-button" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              Complete setup now ›
            </Link>
          </div>
        )}
        {!landlordSigned && !hasSignedLease && (
          <p style={{ fontSize: '0.72rem', color: '#9CA3AF', margin: '0.4rem 0 0' }}>
            Accepted formats: PDF, DOCX · Max 10MB
          </p>
        )}
        {leaseGenerateError && <p className="error-message" style={{ marginTop: '0.5rem' }}>{leaseGenerateError}</p>}

        {/* Blank lease download */}
        <div style={{
          marginTop: '0.85rem', paddingTop: '0.75rem', borderTop: '1px solid #E6EEF2',
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.82rem', color: '#4B5563' }}>Prefer to fill it out yourself?</span>
              <a
                href={`${process.env.REACT_APP_API_BASE_URL}/api/lease-templates/ontario-standard`}
                download
                className="link-button"
                style={{ fontSize: '0.82rem', fontWeight: 500 }}
              >
                Download blank Ontario Standard Lease (Form 2229E) ↓
              </a>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#9CA3AF', margin: '0.2rem 0 0' }}>
              Fillable PDF — open in Chrome or Adobe Acrobat (macOS Preview does not support form fields).
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        {(leaseDocs?.length > 0) && (
          <div style={{
            marginTop: '0.75rem', padding: '0.6rem 0.85rem',
            background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px',
            fontSize: '0.78rem', color: '#92400E', lineHeight: 1.45
          }}>
            <strong>Please review your lease carefully before signing.</strong> Rentual auto-fills
            lease details from your property settings and application data as a convenience, but
            does not guarantee accuracy. You are responsible for verifying all terms, amounts,
            and dates. Rentual is not liable for errors or omissions in generated leases.
          </div>
        )}
      </div>

      {/* Step 2: Send for signatures */}
      <div className={`lease-section${!hasLeaseDoc ? ' lease-section--dimmed' : ''}`}>
        <h3>2. Send for Signatures</h3>
        {!hasLeaseDoc ? (
          <p className="status-text">Upload or generate a lease first.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.5rem' }}>
              {!landlordSigned && !hasSignedLease && (
                <button className="success-button" type="button" onClick={handleSignAsLandlord}>
                  ✍️ Sign as Landlord
                </button>
              )}
              {landlordSigned && !hasSignedLease && (
                <p className="status-text" style={{ color: '#16A34A', fontWeight: 600 }}>✅ You've signed. Waiting for tenant to sign.</p>
              )}
            </div>
            {esignError && <p className="error-message" style={{ fontSize: '0.85rem' }}>{esignError}</p>}
          </>
        )}
      </div>

      {/* Step 3: Signing status */}
      <div className={`lease-section${!esignRequest ? ' lease-section--dimmed' : ''}`}>
        <h3>3. Signing Status</h3>
        {!esignRequest && !hasLeaseDoc ? (
          <p className="status-text">Upload or generate a lease to begin the signing process.</p>
        ) : !esignRequest ? (
          <p className="status-text">Send the lease for signatures to begin.</p>
        ) : (
          <>
            <div className="signing-status-list">
              <div className={`signing-status-item${landlordSigned ? ' signing-status-item--signed' : ''}`}>
                <span className="signing-status-icon">{landlordSigned ? '✅' : esignRequest ? '⏳' : '⬜'}</span>
                <span className="signing-status-label">Landlord signature {application?.full_name ? '' : ''}</span>
                <span className="signing-status-value">{landlordSigned ? 'Signed' : esignRequest ? 'Awaiting' : 'Not started'}</span>
              </div>
              <div className={`signing-status-item${hasSignedLease ? ' signing-status-item--signed' : ''}`}>
                <span className="signing-status-icon">{hasSignedLease ? '✅' : esignRequest ? '⏳' : '⬜'}</span>
                <span className="signing-status-label">Tenant signature — {application?.full_name || 'Tenant'}</span>
                <span className="signing-status-value">{hasSignedLease ? 'Signed' : esignRequest ? 'Awaiting' : 'Not started'}</span>
              </div>
            </div>
            <p className="status-text" style={{ marginTop: '0.5rem' }}>
              <strong>Overall:</strong> {leaseStatusLabel}
            </p>
          </>
        )}
      </div>

      {/* Step 4: Completed */}
      <div className={`lease-section${!(hasSignedLease && landlordSigned) ? ' lease-section--dimmed' : ''}`}>
        <h3>4. Completed</h3>
        {isCompleted ? (
          <div className="lease-completed-banner">
            <p>🎉 Lease fully executed</p>
            <p className="status-text" style={{ margin: '0 0 0.75rem' }}>Both parties have signed. The lease is now active.</p>
            {leaseDocs?.[0] && (
              <button className="primary-button" type="button" onClick={() => {
                const token = localStorage.getItem('token');
                window.open(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/leases/${leaseDocs[0].id}/download?token=${token}`, '_blank');
              }}>
                📄 Download Signed Lease
              </button>
            )}
          </div>
        ) : hasSignedLease && landlordSigned ? (
          <div style={{ textAlign: 'center' }}>
            <p className="status-text" style={{ margin: '0 0 0.75rem' }}>Both signatures are in. Mark this application as completed.</p>
            <button className="primary-button" type="button" onClick={handleMarkComplete}>
              Mark as Completed
            </button>
          </div>
        ) : (
          <p className="status-text">Waiting for all signatures before completing.</p>
        )}
      </div>

      {/* E-sign iframe modal */}
      {esignModalUrl && (
        <div className="esign-modal-overlay" onClick={() => setEsignModalUrl('')}>
          <div className="esign-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="esign-modal-header">
              <span style={{ fontWeight: 600 }}>E-Sign Document</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button type="button" className="success-button" style={{ fontSize: '0.82rem', padding: '0.35rem 0.85rem' }} onClick={() => { setEsignModalUrl(''); fetchData(); }}>
                  ✅ Done Signing
                </button>
                <button type="button" className="ghost-button" onClick={() => setEsignModalUrl('')}>✕</button>
              </div>
            </div>
            <iframe src={esignModalUrl} title="E-Sign" className="esign-modal-iframe" />
          </div>
        </div>
      )}

    </main>
  );
}

export default LeaseSigning;
